import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", email: "", password: "", mobile: "", city: "", state: "", profession: "",
  });
  const [industries, setIndustries] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/meta").then(({ data }) => setIndustries(data.industries)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await register(form);
      toast.success(`Welcome to Vishwakarma Nexus, ${u.name.split(" ")[0]}!`);
      navigate("/profile/edit?welcome=1");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-nx flex min-h-[80vh] items-center justify-center py-12">
      <div className="w-full max-w-lg">
        <div className="card-nx p-8 animate-fade-up">
          <div className="flex justify-center"><Logo /></div>
          <h1 className="mt-6 text-center text-2xl font-bold text-[#0B192C] font-head">Join the Community</h1>
          <p className="mt-2 text-center text-sm text-slate-500">
            It takes under a minute. Only the essentials to get you started.
          </p>

          <form onSubmit={submit} className="mt-7 space-y-4" data-testid="register-form">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-[#0B192C]">Full name *</label>
              <input required value={form.name} onChange={set("name")} className="input-nx"
                placeholder="e.g. Rajesh Vishwakarma" data-testid="register-name-input" />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-[#0B192C]">Email *</label>
                <input type="email" required value={form.email} onChange={set("email")} className="input-nx"
                  placeholder="you@example.com" data-testid="register-email-input" />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-[#0B192C]">Mobile number</label>
                <input value={form.mobile} onChange={set("mobile")} className="input-nx"
                  placeholder="Optional" data-testid="register-mobile-input" />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-[#0B192C]">City</label>
                <input value={form.city} onChange={set("city")} className="input-nx"
                  placeholder="e.g. Pune" data-testid="register-city-input" />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-[#0B192C]">State</label>
                <input value={form.state} onChange={set("state")} className="input-nx"
                  placeholder="e.g. Maharashtra" data-testid="register-state-input" />
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-[#0B192C]">Primary profession / category</label>
              <Select value={form.profession} onValueChange={(v) => setForm((f) => ({ ...f, profession: v }))}>
                <SelectTrigger className="input-nx h-auto" data-testid="register-profession-select">
                  <SelectValue placeholder="Select your industry" />
                </SelectTrigger>
                <SelectContent>
                  {industries.map((i) => (
                    <SelectItem key={i} value={i} data-testid={`register-profession-${i}`}>{i}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-[#0B192C]">Password *</label>
              <input type="password" required minLength={6} value={form.password} onChange={set("password")}
                className="input-nx" placeholder="At least 6 characters" data-testid="register-password-input" />
            </div>
            {error && <p className="text-sm text-red-600" data-testid="register-error">{error}</p>}
            <button type="submit" disabled={loading} className="btn-gold w-full" data-testid="register-submit-btn">
              {loading ? "Creating account..." : "Create my account"}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-slate-500">
            Already a member?{" "}
            <Link to="/login" className="font-semibold text-[#0B192C] hover:underline" data-testid="register-to-login">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
